import React from 'react';
import { useStyles } from '@archipro-design/aria';
import * as S from './CollapsibleContainer.style';
import { useIsomorphicLayoutEffect } from 'ahooks';

const MIN_TRANSITION_DURATION = 100; //ms
const MAX_TRANSITION_DURATION = 500; //ms
const TRANSITION_SPEED = 120 / 250; // 150ms for 250px

interface Props extends React.HTMLAttributes<HTMLDivElement> {
    children: React.ReactNode;
    visible: boolean;
}
const CollapsibleContainer = ({ visible, children, ...rest }: Props) => {
    const [contentHeight, setContentHeight] = React.useState('0');
    const [childrenVisible, setChildrenVisible] = React.useState(visible);
    const [transitionDuration, setTransitionDuration] = React.useState(150);
    const { css } = useStyles({
        contentHeight,
        transitionDuration,
    });

    const ref = React.useRef<HTMLDivElement>(null);

    const applyContentHeight = React.useCallback(() => {
        const container = ref.current;
        if (container) {
            setContentHeight(container.scrollHeight + 'px');
            let duration = Math.round(
                container.scrollHeight * TRANSITION_SPEED
            );
            duration = Math.min(MAX_TRANSITION_DURATION, duration);
            duration = Math.max(MIN_TRANSITION_DURATION, duration);
            setTransitionDuration(duration);
        }
    }, [ref]);

    useIsomorphicLayoutEffect(() => {
        applyContentHeight();
        if (visible) {
            setChildrenVisible(visible);
        } else {
            const timer = setTimeout(() => {
                setContentHeight('0px');
            });
            return () => {
                clearTimeout(timer);
            };
        }
    }, [visible, applyContentHeight]);

    const onTransitionEnd = () => {
        setChildrenVisible(visible);
        if (visible) {
            setContentHeight('unset');
        }
    };

    return (
        <div
            className={css(S.CollapsibleContainer)}
            ref={ref}
            {...rest}
            onTransitionEnd={onTransitionEnd}
        >
            {(visible || childrenVisible) && children}
        </div>
    );
};

export default CollapsibleContainer;
